import { isEqual } from 'lodash-es';
import { memo, useCallback, useMemo } from 'react';
import { FormattedPlural, defineMessages } from 'react-intl';
import {
  useDynamicCallback,
  useGetCustomerMarketAccountLabel,
  useIntl,
  useWLTradableCustomerMarketAccounts,
} from '../../hooks';
import { useWLCustomerMarketAccountContext } from '../../providers/WLCustomerMarketAccountsProvider';
import type { CustomerMarketAccount } from '../../types';
import { EMPTY_ARRAY } from '../../utils';
import { Box, HStack } from '../Core';
import { SearchSelect } from '../Form';
import type { AutocompleteGroup, AutocompleteSearchResult } from '../Form/Autocomplete';
import { IndicatorBadge } from '../IndicatorBadge';
import { FormattedMessage } from '../Intl';
import { Text } from '../Text';
import { useCustomerMarketAccountResults } from './hooks';
import type { CustomerMarketAccountSelectorProps } from './types';

const messages = defineMessages({
  selectAccount: {
    defaultMessage: 'Select Account',
    id: 'CustomerMarketAccountSelector.selectAccount',
  },
  maxAvailable: {
    defaultMessage: 'Max Available',
    id: 'CustomerMarketAccountSelector.maxAvailable',
  },
  account: {
    defaultMessage: 'Account',
    id: 'CustomerMarketAccountSelector.account',
  },
  accounts: {
    defaultMessage: 'Accounts',
    id: 'CustomerMarketAccountSelector.accounts',
  },
});

const MARKET_ACCOUNT_RESULT_HEIGHT = 55;
const LIST_HEIGHT = MARKET_ACCOUNT_RESULT_HEIGHT * 5;

const matchesCustomerMarketAccountSourceID = (item: CustomerMarketAccount, searchString: string) =>
  item.SourceAccountID.toLowerCase().includes(searchString);

export const CustomerMarketAccountSelector = memo(
  function CustomerMarketAccountSelector({
    accountID,
    availableCustomerMarketAccounts,
    disabled,
    error,
    touched,
    onChangeMarketAccount,
    security,
  }: CustomerMarketAccountSelectorProps) {
    const { customerMarketAccountsList } = useWLCustomerMarketAccountContext();
    const CustomerMarketAccountResult = useCustomerMarketAccountResults<CustomerMarketAccount>(security);
    const { formatMessage } = useIntl();

    const tradableAccounts = useWLTradableCustomerMarketAccounts();
    const getCustomerMarketAccountLabel = useGetCustomerMarketAccountLabel();

    // Passing in availableCustomerMarketAccounts, now optional, overrides the default accounts.
    const searchableItems = (
      availableCustomerMarketAccounts ? availableCustomerMarketAccounts : (customerMarketAccountsList ?? EMPTY_ARRAY)
    ).filter(account => !tradableAccounts || tradableAccounts.has(account.SourceAccountID));

    const customerMarketAccount = useMemo(
      () => searchableItems?.find?.(s => s.SourceAccountID === accountID),
      [searchableItems, accountID]
    );

    const handleChange = useCallback(
      (account?: CustomerMarketAccount) => {
        if (account) {
          onChangeMarketAccount(account);
        }
      },
      [onChangeMarketAccount]
    );

    const RenderGroupHeader = useDynamicCallback(
      ({ group }: { group: AutocompleteGroup<AutocompleteSearchResult<CustomerMarketAccount>> }) => {
        const numResults = group.items.length;
        return (
          <HStack fontSize="fontSizeXs" justifyContent="space-between" w="100%" p="spacing8">
            <Box>
              <HStack gap="spacing4">
                <Text transform="uppercase">
                  <FormattedPlural
                    value={numResults}
                    one={<FormattedMessage {...messages.account} />}
                    other={<FormattedMessage {...messages.accounts} />}
                  />
                </Text>
                <IndicatorBadge show={numResults > 0} children={numResults} />
              </HStack>
            </Box>
            <Text transform="uppercase">
              <FormattedMessage {...messages.maxAvailable} />
            </Text>
          </HStack>
        );
      }
    );

    // We only have one group
    const getGroup = useDynamicCallback((acc: CustomerMarketAccount) => 'Account');

    return (
      <SearchSelect
        data-testid="customer-market-account-selector"
        options={searchableItems}
        selection={customerMarketAccount}
        getLabel={({ SourceAccountID }) => getCustomerMarketAccountLabel(SourceAccountID)}
        onChange={handleChange}
        invalid={!!error}
        touched={touched}
        disabled={disabled}
        additionalSearchMatcher={matchesCustomerMarketAccountSourceID}
        inputValueChangeOnItemSelection="clear"
        renderResult={CustomerMarketAccountResult}
        RenderGroupHeader={RenderGroupHeader}
        itemSize={MARKET_ACCOUNT_RESULT_HEIGHT}
        maxHeight={LIST_HEIGHT}
        groupMaxHeight={LIST_HEIGHT}
        placeholder={formatMessage(messages.selectAccount)}
        getGroup={getGroup}
      />
    );
  },
  (p, n) => isEqual(p, n)
);
